import React from 'react';
import {
    Page,
    Header,
    Content,
    InfoCard,
    Link,
} from '@backstage/core-components';
// Note: configApiRef removed - using hardcoded proxy path instead of config
// because custom config keys require schema definition for frontend visibility
import { Grid, Typography, Button, makeStyles, Chip, Divider } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import CodeIcon from '@material-ui/icons/Code';
import DescriptionIcon from '@material-ui/icons/Description';

const useStyles = makeStyles(theme => ({
    downloadButton: {
        marginTop: theme.spacing(2),
        marginRight: theme.spacing(1),
    },
    toolCard: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    cardContent: {
        flexGrow: 1,
    },
    icon: {
        fontSize: 48,
        marginBottom: theme.spacing(2),
        color: theme.palette.primary.main,
    },
    chip: {
        marginRight: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
    },
    version: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
}));

interface ToolDownload {
    name: string;
    description: string;
    version?: string;
    icon: React.ReactNode;
    downloads: {
        label: string;
        url: string;
        filename?: string;
    }[];
    tags?: string[];
    docsUrl?: string;
}

export const ToolsPage = () => {
    const classes = useStyles();

    // Base URL for downloads - uses the tools-download backend plugin
    // which fetches from S3: s3://causeway-${ENVIRONMENT}-developers/backstage-catalog-v2/tools/
    // using AWS SDK with IAM role credentials (works on Fargate)
    const baseDownloadUrl = '/api/tools-download';

    // Tools are stored in S3 at: s3://causeway-${ENVIRONMENT}-developers/backstage-catalog-v2/tools/
    // Structure:
    //   /postman/causewayapi.postman_collection.json
    //   /sdk/nodejs/causeway-sdk-1.0.0.tgz
    //   /sdk/python/causeway_sdk-1.0.0-py3-none-any.whl
    //   /sdk/java/causeway-java-sdk-1.0.0.zip
    const collections: ToolDownload[] = [
        {
            name: 'Postman Collection',
            description: 'Import our API collection into Postman to quickly test and explore the CausewayOne APIs. Includes pre-configured environments for dev, staging, and production.',
            version: '2.1',
            icon: <DescriptionIcon className={classes.icon} />,
            downloads: [
                {
                    label: 'Download Collection',
                    url: `${baseDownloadUrl}/postman/causewayapi.postman_collection.json`,
                    filename: 'causewayapi.postman_collection.json',
                },
            ],
            tags: ['API Testing', 'Postman'],
            docsUrl: 'https://learning.postman.com/docs/getting-started/importing-and-exporting-data/',
        },
    ];

    const sdks: ToolDownload[] = [
        {
            name: '.NET SDK',
            description: 'Official .NET SDK for integrating with CausewayOne APIs. Supports .NET 6+ with full async/await support and strong typing.',
            version: '1.0.0',
            icon: <CodeIcon className={classes.icon} />,
            downloads: [
                {
                    label: 'Download SDK (.zip)',
                    url: `${baseDownloadUrl}/sdk/dotnet/causeway-dotnet-sdk-1.0.0.zip`,
                    filename: 'causeway-dotnet-sdk-1.0.0.zip',
                },
            ],
            tags: ['.NET', 'C#', 'SDK'],
            docsUrl: '/documentation',
        },
        {
            name: 'Java SDK',
            description: 'Official Java SDK for integrating with CausewayOne APIs. Supports Java 11+ with Maven and Gradle build systems.',
            version: '1.0.0',
            icon: <CodeIcon className={classes.icon} />,
            downloads: [
                {
                    label: 'Download SDK (.zip)',
                    url: `${baseDownloadUrl}/sdk/java/causeway-java-sdk-1.0.0.zip`,
                    filename: 'causeway-java-sdk-1.0.0.zip',
                },
            ],
            tags: ['Java', 'SDK', 'Maven'],
            docsUrl: '/documentation',
        },
        {
            name: 'Node.js SDK',
            description: 'Official Node.js SDK for integrating with CausewayOne APIs. Supports TypeScript and includes type definitions. Compatible with Node.js 18+.',
            version: '1.0.0',
            icon: <CodeIcon className={classes.icon} />,
            downloads: [
                {
                    label: 'Download SDK (.zip)',
                    url: `${baseDownloadUrl}/sdk/nodejs/causeway-nodejs-sdk-1.0.0.zip`,
                    filename: 'causeway-nodejs-sdk-1.0.0.zip',
                },
            ],
            tags: ['Node.js', 'TypeScript', 'SDK'],
            docsUrl: '/documentation',
        },
        {
            name: 'Python SDK',
            description: 'Official Python SDK for integrating with CausewayOne APIs. Supports Python 3.8+ with full type hints and async support.',
            version: '1.0.0',
            icon: <CodeIcon className={classes.icon} />,
            downloads: [
                {
                    label: 'Download SDK (.zip)',
                    url: `${baseDownloadUrl}/sdk/python/causeway-python-sdk-1.0.0.zip`,
                    filename: 'causeway-python-sdk-1.0.0.zip',
                },
            ],
            tags: ['Python', 'SDK', 'Async'],
            docsUrl: '/documentation',
        },
    ];

    const handleDownload = async (url: string, filename?: string) => {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Download failed: ${response.statusText}`);
            }
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Download failed:', error);
            // Fallback: open in new tab
            window.open(url, '_blank');
        }
    };

    return (
        <Page themeId="tool">
            <Header
                title="Developer Tools"
                subtitle="SDKs, collections, and utilities to help you integrate with CausewayOne APIs"
            />
            <Content>
                <Grid container spacing={3}>
                    {collections.map(tool => (
                        <Grid item xs={12} md={12} key={tool.name}>
                            <InfoCard
                                title={tool.name}
                                className={classes.toolCard}
                            >
                                <div className={classes.cardContent}>
                                    {tool.icon}
                                    <Typography variant="body2" paragraph>
                                        {tool.description}
                                    </Typography>
                                    {tool.version && (
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            className={classes.version}
                                            component="div"
                                        >
                                            Version: {tool.version}
                                        </Typography>
                                    )}
                                    {tool.tags && (
                                        <div>
                                            {tool.tags.map(tag => (
                                                <Chip
                                                    key={tag}
                                                    label={tag}
                                                    size="small"
                                                    className={classes.chip}
                                                />
                                            ))}
                                        </div>
                                    )}
                                    <div>
                                        {tool.downloads.map(download => (
                                            <Button
                                                key={download.label}
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                className={classes.downloadButton}
                                                startIcon={<GetAppIcon />}
                                                onClick={() =>
                                                    handleDownload(download.url, download.filename)
                                                }
                                            >
                                                {download.label}
                                            </Button>
                                        ))}
                                    </div>
                                    {tool.docsUrl && (
                                        <Typography
                                            variant="body2"
                                            style={{ marginTop: 16 }}
                                        >
                                            <Link to={tool.docsUrl}>View Documentation</Link>
                                        </Typography>
                                    )}
                                </div>
                            </InfoCard>
                        </Grid>
                    ))}
                </Grid>
                <Divider style={{ margin: '24px 0' }} />
                <Typography variant="h6" style={{ marginBottom: 16 }}>
                    SDKs
                </Typography>
                <Grid container spacing={3}>
                    {sdks.map(tool => (
                        <Grid item xs={12} md={6} key={tool.name}>
                            <InfoCard
                                title={tool.name}
                                className={classes.toolCard}
                            >
                                <div className={classes.cardContent}>
                                    {tool.icon}
                                    <Typography variant="body2" paragraph>
                                        {tool.description}
                                    </Typography>
                                    {tool.version && (
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            className={classes.version}
                                            component="div"
                                        >
                                            Version: {tool.version}
                                        </Typography>
                                    )}
                                    {tool.tags && (
                                        <div>
                                            {tool.tags.map(tag => (
                                                <Chip
                                                    key={tag}
                                                    label={tag}
                                                    size="small"
                                                    className={classes.chip}
                                                />
                                            ))}
                                        </div>
                                    )}
                                    <div>
                                        {tool.downloads.map(download => (
                                            <Button
                                                key={download.label}
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                className={classes.downloadButton}
                                                startIcon={<GetAppIcon />}
                                                onClick={() =>
                                                    handleDownload(download.url, download.filename)
                                                }
                                            >
                                                {download.label}
                                            </Button>
                                        ))}
                                    </div>
                                    {tool.docsUrl && (
                                        <Typography
                                            variant="body2"
                                            style={{ marginTop: 16 }}
                                        >
                                            <Link to={tool.docsUrl}>View Documentation</Link>
                                        </Typography>
                                    )}
                                </div>
                            </InfoCard>
                        </Grid>
                    ))}
                </Grid>
            </Content>
        </Page>
    );
};
